import {
  ChangePasswordCompleteSuccess,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ChangePasswordComplete } from 'services/graphql/mutations/ChangePasswordComplete';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { deleteRefreshTokenForReactNativeUserKey } from 'shared/utils/biometricAuth/biometricAuth';
import { useMutation } from 'urql';

export interface IConfirmPassword {
  fetching: boolean;
  passwordVisible: {
    newPassword: boolean;
    confirmNewPassword: boolean;
  };
  isErrorShown: boolean;
  errorDetails: {
    cancelButtonTitle: string;
    defaultTitle: string;
    errorResponse: string;
  };
  completePasswordChanges: (newPassword: string) => Promise<void>;
  setPasswordVisible: Dispatch<
    SetStateAction<{ newPassword: boolean; confirmNewPassword: boolean }>
  >;
  setIsErrorScreenShown: Dispatch<SetStateAction<boolean>>;
}

const useConfirmPassword = (): IConfirmPassword => {
  const router = useRouter();
  const [{ fetching }, executeMutation] = useMutation<Mutation>(
    ChangePasswordComplete,
  );

  const [passwordVisible, setPasswordVisible] = useState<{
    newPassword: boolean;
    confirmNewPassword: boolean;
  }>({
    newPassword: false,
    confirmNewPassword: false,
  });

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const [errorResponse, setErrorResponse] = useState(defaultMessage);

  const logChangePasswordCompleted = usePressableAnalytics({
    analyticsId: 'changePasswordCompletedOnPersonalDetails',
  });
  const logChangePasswordFailed = usePressableAnalytics({
    analyticsId: 'resetPasswordFailedInPersonalDetails',
    customAttributes: { failure_reason: errorResponse },
  });

  const [isErrorShown, setIsErrorScreenShown] = useState(false);

  useEffect(() => {
    if (isErrorShown) {
      logChangePasswordFailed();
    }
  }, [isErrorShown, logChangePasswordFailed]);

  const completePasswordChanges = async (
    newPassword: string,
  ): Promise<void> => {
    try {
      const response = await executeMutation({
        input: { newPassword },
      });
      const result = response?.data
        ?.changePasswordComplete as ChangePasswordCompleteSuccess;
      if (result?.__typename === Typenames.ChangePasswordCompleteSuccess) {
        logChangePasswordCompleted();

        await deleteRefreshTokenForReactNativeUserKey();
        router.replace('/myvaro/personal-details/change-password/success');
      } else {
        setErrorResponse(result?.message ?? '');
        setIsErrorScreenShown(true);
      }
    } catch {
      setIsErrorScreenShown(true);
    }
  };
  return {
    fetching,
    passwordVisible,
    isErrorShown,
    errorDetails: {
      defaultTitle,
      errorResponse,
      cancelButtonTitle,
    },
    setPasswordVisible,
    completePasswordChanges,
    setIsErrorScreenShown,
  };
};

export default useConfirmPassword;
